import React from 'react';
import { Users } from 'lucide-react';
import { Accordion } from '@/components/ui/accordion';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PermissionCard, SubPermissionCard, SectionAccordionItem } from '../cards';
import { BulkActionsDropdown } from '../BulkActionsDropdown';

export interface ManagementTabProps {
  viewEmployeeDirectory: boolean;
  onViewEmployeeDirectoryChange: (value: boolean) => void;
  viewEmployeeDirectoryAccess: string;
  onViewEmployeeDirectoryAccessChange: (value: string) => void;
  viewEmployeeDetails: boolean;
  onViewEmployeeDetailsChange: (value: boolean) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export const ManagementTab: React.FC<ManagementTabProps> = ({
  viewEmployeeDirectory,
  onViewEmployeeDirectoryChange,
  viewEmployeeDirectoryAccess,
  onViewEmployeeDirectoryAccessChange,
  viewEmployeeDetails,
  onViewEmployeeDetailsChange,
  onSelectAll,
  onDeselectAll,
}) => (
  <>
    <BulkActionsDropdown onSelectAll={onSelectAll} onDeselectAll={onDeselectAll} />
    <Accordion type="multiple" defaultValue={['employee-directory']} className="space-y-3">
      <SectionAccordionItem value="employee-directory" icon={<Users className="h-5 w-5" />} title="Employee Directory" description="Manage access to employee information and profiles">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="flex flex-col gap-2">
            <PermissionCard
              label={<Label htmlFor="view-employee-directory" className="cursor-pointer">View Employee Directory</Label>}
              description="View the employee directory and basic information"
              onToggle={() => onViewEmployeeDirectoryChange(!viewEmployeeDirectory)}
              control={<Checkbox id="view-employee-directory" checked={viewEmployeeDirectory} onCheckedChange={(c) => onViewEmployeeDirectoryChange(c as boolean)} />}
            />
            {viewEmployeeDirectory && (
              <div className="flex flex-col gap-2">
                <SubPermissionCard
                  label="View Members"
                  description="Define which team members this role can see in the directory"
                  control={
                    <Select value={viewEmployeeDirectoryAccess} onValueChange={onViewEmployeeDirectoryAccessChange}>
                      <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="own">Own Only</SelectItem>
                        <SelectItem value="team">Team (same department)</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                      </SelectContent>
                    </Select>
                  }
                />
              </div>
            )}
          </div>
          <PermissionCard
            label={<Label htmlFor="view-employee-details" className="cursor-pointer">View Employee Details</Label>}
            description="View detailed employee information and profiles"
            onToggle={() => onViewEmployeeDetailsChange(!viewEmployeeDetails)}
            control={<Checkbox id="view-employee-details" checked={viewEmployeeDetails} onCheckedChange={(c) => onViewEmployeeDetailsChange(c as boolean)} />}
          />
        </div>
      </SectionAccordionItem>
    </Accordion>
  </>
);
